<template>
  <div id="markdown-page" data-cy="markdown-page" v-if="ready">
    <div class="content-with-spacing">
      <div class="heading-container">
        <h1 v-if="markdownPageTitle">{{ markdownPageTitle }}</h1>
          <div class="heading-buttons">
            <div v-if="presentationContent" class="toggle-mode-container">
              <k-toggle v-model="presentationModeEnabled" :round="true" size="small" label="Toggle between page mode and presentation mode"></k-toggle>
            </div>
            <module-asset-completion-button :asset="asset"></module-asset-completion-button>
            <intersection-observer observed-element="completion-buttons"
              @on-intersection-element="onElementInView"
            ></intersection-observer>
        </div>
      </div>
      <div v-if="markdownPageContentReady && !presentationModeEnabled"
        id="markdown-page-section"
        data-cy="markdown-page-content"
        ref="pageModeContainer"
        v-html="markdownContentAsHtml"
        class="markdown-page-container e-learning panel"
      ></div>
      <div
        ref="hiddenSectionAnnouncer"
        class="sr-only"
        data-cy="hidden-section-announcer"
        aria-live="polite"
        aria-atomic="true"
      ></div>
      <div v-if="markdownPageContentReady && presentationModeEnabled" class="markdown-page-container panel">
        <KPresentation :markdownContent="presentationContent" />
      </div>
      <div class="bottom-button-container" v-if="!isInViewElement">
        <module-asset-completion-button :asset="asset"></module-asset-completion-button>
      </div>
    </div>
  </div>
</template>

<style>
#markdown-page .content-with-spacing {
  max-width: 960px;
}

/* ||. font-size */
#markdown-page .heading-container ~ div,
#markdown-page h5 {
  font-size: clamp(18px, 2vw, 21px);
}

#markdown-page h4 {
  font-size: clamp(21px, 2vw, 23px);
}

#markdown-page h3 {
  font-size: clamp(23px, 2vw, 25px);
}
/* ||. */

.markdown-page-container img {
  margin: 0 auto;
  display: block;
  max-height: 600px;
  max-width: 70%;
  padding: 25px 0;
}

.markdown-page-container p {
  color: var(--kate-type-primary);
}

/* Need to use un-scoped CSS for it to apply to v-html content */
.markdown-page-container.e-learning > table {
  width: 100%;
  margin-bottom: 1em;
  border: 1px solid var(--kate-background-alt-alpha);
}

.markdown-page-container.e-learning thead tr {
  background-color: var(--kate-background-body);
  border-color: var(--kate-background-alt-alpha);
  color: var(--kate-type-light);
  font-weight: bold;
}

.markdown-page-container.e-learning > table > tbody > tr > td,
.markdown-page-container.e-learning > table > tbody > tr > th,
.markdown-page-container.e-learning > table > tfoot > tr > td,
.markdown-page-container.e-learning > table > tfoot > tr > th,
.markdown-page-container.e-learning > table > thead > tr > td,
.markdown-page-container.e-learning > table > thead > tr > th {
  padding: 0.5em;
  line-height: 1.5;
  vertical-align: top;
  border-top: 1px solid var(--kate-background-alt-alpha);
}

.markdown-page-container.e-learning tbody tr:nth-child(2n) {
  background-color: var(--kate-panel-alt);
}

.markdown-page-container.e-learning h1#markdown-page {
  padding-top: 0;
  margin-top: 0;
}

.markdown-page-container.e-learning > h3,
.markdown-page-container.e-learning > h4 {
  margin-top: 1em;
  margin-bottom: 0.5em;
}

.hidden-outer {
  padding: 20px;
  margin: 15px 0;
  border-radius: 4px;
  background-color: var(--input-background);
}

.hidden-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
}

.hidden-header .hidden-section-title {
  flex: 1;
  margin: 0;
}

.hidden-outer .btn-toggle {
  flex-shrink: 0;
  background-color: transparent;
  color: var(--kate-primary);
  border: 1px solid var(--kate-primary);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.hidden-outer .btn-toggle:focus-visible {
  outline: 2px solid var(--kate-primary);
  outline-offset: 2px;
}

.hidden-outer .btn-toggle:hover {
  background-color: var(--kate-button-secondary-hover);
  color: var(--kate-type-dark);
  border-color: var(--kate-button-secondary-hover);
}

.hidden-outer .btn-toggle:active,
.btn-toggle[aria-expanded="true"] {
  background-color: var(--kate-primary);
  color: var(--kate-type-dark);
}

.hidden-outer .hidden-inner {
  padding: 15px;
  margin-top: 15px;
  border-radius: 4px;
}

.hidden-outer .hidden-inner[hidden] {
  display: none;
}

.markdown-page-container.panel ol li,
.markdown-page-container.panel ul li {
  color: var(--kate-type-primary);
}

/* === Notes === */
.markdown-page-container .kate-note {
  background-color: var(--kate-note);
  padding: 1.2em;
  margin: 0 15px 15px;
  border-radius: 4px;
  position: relative;
  box-shadow: var(--box-shadow);
}

.markdown-page-container .kate-note::before {
  right: 100%;
  border: solid transparent;
  content: " ";
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
  border-right-color: var(--kate-note);
  border-width: 10px;
  top: 10px;
}

/* === Print === */
@media print {
  header,
  aside,
  .heading-container,
  .buttons-container.sticky,
  .chat-box-container,
  .feedback-form-container,
  footer,
  nav {
    display: none !important;
  }

  #app,
  body {
    overflow: visible;
    visibility: hidden;
  }

  .sidebar-page-content.sidebar-open {
    padding-left: 0;
  }

  #markdown-page-section {
    display: block;
    position: relative;
    width: auto;
    height: auto;
    overflow: visible;
    visibility: visible;
    margin: 0;
    page-break-inside: always;
  }
}

</style>

<style scoped>
.heading-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
}

.toggle-mode-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.markdown-page-container {
  padding: 25px;
}

.markdown-page-container.panel.panel-default {
  display: flex;
  flex: 1;
}

.category-header {
  margin-bottom: 20px;
}

</style>

<script>
import PageReadyMixin from '@mixins/page-ready-mixin';
import KToggle from '@base-components/forms/k-toggle.vue';
import KPresentation from '@base-components/k-presentation.vue';
import { markdown } from '@utils/maths-markdown';
import IntersectionObserver from '@base-components/k-intersection-observer.vue';
import ModuleAssetCompletionButton from './module-asset-completion-button.vue';

export default {
  components: {
    KPresentation,
    KToggle,
    ModuleAssetCompletionButton,
    IntersectionObserver,
  },

  mixins: [PageReadyMixin],

  props: {
    asset: {
      type: Object,
    },
    sidebarReady: {
      type: Boolean,
    },
  },

  data() {
    return {
      presentationModeEnabled: false,
      markdownPageContentReady: false,
      pageContent: undefined,
      presentationContent: undefined,
      isInViewElement: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getContent();
  },

  watch: {
    assetId() {
      this.getContent();
    },

    renderingInPageMode() {
      if (this.renderingInPageMode) {
        this.setupPageModeContent();
      }
    },

    pageContent() {
      if (this.renderingInPageMode) {
        this.setupPageModeContent();
      }
    },

    presentationModeEnabled() {
      this.$logger.info(`Viewing markdown page in ${this.presentationModeEnabled ? 'presentation' : 'page'} mode`);
    },
  },

  computed: {
    ready() {
      return Boolean(this.asset && this.markdownPageContentReady && this.sidebarReady);
    },

    assetId() {
      return this.asset?.id;
    },

    markdownPageTitle() {
      return this.asset?.name;
    },

    markdownContentAsHtml() {
      return this.markdownToHtml(this.pageContent);
    },

    renderingInPageMode() {
      return this.ready && !this.presentationModeEnabled;
    },
  },

  methods: {
    setupPageModeContent() {
      this.$nextTick(() => {
        const container = this.$refs.pageModeContainer;
        if (!container) {
          return;
        }
        this.bindHiddenSectionToggles(container);
        this.enhanceMarkdownLinks(container);
      });
    },

    enhanceMarkdownLinks(container) {
      container.querySelectorAll('a[href]').forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');

        if (link.querySelector('.sr-only')) {
          return;
        }

        link.appendChild(document.createTextNode(' '));
        const hint = document.createElement('span');
        hint.className = 'sr-only';
        hint.textContent = '(opens in new tab)';
        link.appendChild(hint);
      });
    },

    bindHiddenSectionToggles(container) {
      container.querySelectorAll('[data-cy="hidden-section-toggle"]').forEach(button => {
        if (button.hasAttribute('data-hidden-toggle-bound')) {
          return;
        }
        button.setAttribute('data-hidden-toggle-bound', 'true');

        const panelId = button.getAttribute('aria-controls');
        if (!panelId) {
          return;
        }

        const panel = container.querySelector(`[id="${panelId}"]`);
        if (!panel) {
          return;
        }

        const titleText = this.getHiddenSectionTitle(button);
        this.syncHiddenPanelState(button, panel);

        button.addEventListener('click', () => {
          this.toggleHiddenPanel(button, panel, titleText);
        });

        button.addEventListener('keydown', event => {
          if (event.key === 'Escape' && !panel.hasAttribute('hidden')) {
            event.preventDefault();
            this.setHiddenPanelVisibility(panel, true);
            this.syncHiddenPanelState(button, panel);
            this.clearHiddenSectionAnnouncement();
            button.focus();
          }
        });
      });
    },

    getHiddenSectionTitle(button) {
      const titleEl = button.closest('.hidden-outer')?.querySelector('.hidden-section-title');
      return titleEl?.textContent?.trim() || '';
    },

    getHiddenPanelReadableText(panel) {
      const blocks = panel.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, td, th, blockquote, dt, dd');
      if (blocks.length) {
        return Array.from(blocks)
          .map(element => element.textContent.trim())
          .filter(Boolean)
          .join('. ');
      }

      return panel.textContent.replace(/\s+/g, ' ').trim();
    },

    getHiddenSectionAnnouncement(titleText, panel) {
      const contentText = this.getHiddenPanelReadableText(panel);
      if (titleText && contentText) {
        return `${titleText}. ${contentText}`;
      }
      return titleText || contentText;
    },

    announceHiddenSectionExpanded(titleText, panel) {
      const announcer = this.$refs.hiddenSectionAnnouncer;
      if (!announcer) {
        return;
      }

      announcer.textContent = '';
      this.$nextTick(() => {
        announcer.textContent = this.getHiddenSectionAnnouncement(titleText, panel);
      });
    },

    clearHiddenSectionAnnouncement() {
      const announcer = this.$refs.hiddenSectionAnnouncer;
      if (announcer) {
        announcer.textContent = '';
      }
    },

    toggleHiddenPanel(button, panel, titleText) {
      const isHidden = panel.hasAttribute('hidden');
      this.setHiddenPanelVisibility(panel, !isHidden);
      this.syncHiddenPanelState(button, panel);

      if (!panel.hasAttribute('hidden')) {
        this.announceHiddenSectionExpanded(titleText, panel);
      } else {
        this.clearHiddenSectionAnnouncement();
      }
    },

    syncHiddenPanelState(button, panel) {
      button.setAttribute('aria-expanded', String(!panel.hasAttribute('hidden')));
    },

    setHiddenPanelVisibility(panel, isHidden) {
      if (isHidden) {
        panel.setAttribute('hidden', '');
      } else {
        panel.removeAttribute('hidden');
      }
    },

    markdownToHtml(snippet) {
      if (snippet) {
        return markdown(snippet);
      }
      return undefined;
    },

    getContent() {
      if (this.assetId) {
        this.$logger.info('Getting markdown page content', { moduleMarkdownPageId: this.assetId }, true);
        this.$http.get(`/api/curriculum/markdown_page/${this.assetId}`)
          .then(result => {
            this.$logger.info('Got markdown content', { moduleMarkdownPageId: this.assetId });
            this.pageContent = result.data.page;
            this.presentationContent = result.data.presentation;
          })
          .catch(err => {
            this.$logger.autowarn('Error getting markdown content', { moduleMarkdownPageId: this.assetId }, err);
            this.$ktoast.showError(err);
          })
          .then(() => {
            this.markdownPageContentReady = true;
          });
      }
    },

    onElementInView(value) {
      this.isInViewElement = value;
    },
  },
};
</script>
